import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Award, Plus, Trash2, Loader2, Send } from 'lucide-react';
import { PageHeader, Field } from '../components/common';
import { useRealtime } from '../realtime/RealtimeContext';
import { useI18n } from '../i18n/I18nContext';
import { AWARD_ICONS, AWARD_COLORS, awardIcon, awardColor } from '../utils/awardStyles';
import api from '../api/client';

const EMPTY = { operator: '', title: '', description: '', icon: 'trophy', color: 'gold' };

const TEMPLATES = [
  { key: 'safety', icon: 'star', color: 'green' },
  { key: 'producer', icon: 'trophy', color: 'gold' },
  { key: 'zeroDefect', icon: 'gem', color: 'blue' },
  { key: 'teamPlayer', icon: 'heart', color: 'pink' },
  { key: 'attendance', icon: 'crown', color: 'purple' },
  { key: 'speed', icon: 'zap', color: 'orange' },
];

export default function ManageAwards() {
  const { t } = useI18n();
  const [operators, setOperators] = useState([]);
  const [awards, setAwards] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    Promise.all([
      api.listUsers().then((u) => setOperators(u.filter((x) => x.role === 'OPERATOR'))).catch(() => {}),
      api.listAwards().then(setAwards).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useRealtime(() => api.listAwards().then(setAwards).catch(() => {}));

  const give = async () => {
    if (!form.operator) { toast.error(t('ma.pickOperator')); return; }
    if (!form.title.trim()) { toast.error(t('ma.needTitle')); return; }
    setSaving(true);
    try {
      const created = await api.createAward(form);
      setAwards((p) => [created, ...p]);
      toast.success(t('ma.given').replace('{op}', created.operator), { description: created.title });
      setForm({ ...EMPTY, icon: form.icon, color: form.color });
    } catch (e) { toast.error(e?.response?.data?.detail || t('ma.failed')); }
    finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(t('ma.removeConfirm').replace('{title}', a.title).replace('{op}', a.operator))) return;
    setAwards((p) => p.filter((x) => x.id !== a.id));
    try { await api.deleteAward(a.id); toast.success(t('ma.removed')); }
    catch (e) { toast.error(t('common.deleteFailed')); load(); }
  };

  const PreviewIcon = awardIcon(form.icon);
  const applyTemplate = (tpl) => setForm((f) => ({ ...f, title: t(`ma.tpl.${tpl.key}.title`), description: t(`ma.tpl.${tpl.key}.desc`), icon: tpl.icon, color: tpl.color }));

  return (
    <div className="kmt-fade-in">
      <PageHeader icon={Award} title={t('ma.title')} subtitle={t('ma.subtitle')} />
      <div className="p-8 space-y-6">
        {/* Create / give award */}
        <div className="kmt-panel p-6" data-testid="create-award-panel">
          <div className="flex items-center gap-2 mb-5">
            <Plus size={17} style={{ color: 'var(--kmt-gold)' }} />
            <div className="font-display font-semibold">{t('ma.createTitle')}</div>
          </div>
          {/* Quick templates */}
          <div className="mb-5">
            <div className="kmt-label mb-2">{t('ma.templates')}</div>
            <div className="flex flex-wrap gap-2" data-testid="award-templates">
              {TEMPLATES.map((tpl) => {
                const Ico = AWARD_ICONS[tpl.icon];
                return (
                  <button key={tpl.key} onClick={() => applyTemplate(tpl)} data-testid={`award-template-${tpl.key}`}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors hover:border-[var(--kmt-gold)]"
                    style={{ background: 'var(--kmt-panel-2)', border: '1px solid var(--kmt-border)' }}>
                    <Ico size={14} style={{ color: awardColor(tpl.color) }} />
                    {t(`ma.tpl.${tpl.key}.title`)}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <Field label={t('ma.operator')}>
                <select className="kmt-input" value={form.operator} onChange={(e) => setForm({ ...form, operator: e.target.value })} data-testid="award-operator-select">
                  <option value="">{t('ma.pickOperator')}</option>
                  {operators.map((o) => <option key={o.id} value={o.name}>{o.name}</option>)}
                </select>
              </Field>
              <Field label={t('ma.awardTitle')}>
                <input className="kmt-input" value={form.title} maxLength={60} placeholder={t('ma.titlePlaceholder')} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="award-title-input" />
              </Field>
              <Field label={t('ma.description')}>
                <textarea className="kmt-input" rows={2} maxLength={200} placeholder={t('ma.descPlaceholder')} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="award-desc-input" />
              </Field>
              <div>
                <div className="kmt-label mb-2">{t('ma.icon')}</div>
                <div className="flex flex-wrap gap-2" data-testid="award-icon-picker">
                  {Object.keys(AWARD_ICONS).map((k) => {
                    const Ico = AWARD_ICONS[k];
                    const active = form.icon === k;
                    return (
                      <button key={k} onClick={() => setForm({ ...form, icon: k })} data-testid={`award-icon-${k}`}
                        className="w-10 h-10 rounded-lg flex items-center justify-center transition-colors"
                        style={{ background: active ? 'rgba(240,169,43,0.15)' : 'var(--kmt-panel-2)', border: `1px solid ${active ? 'var(--kmt-gold)' : 'var(--kmt-border)'}` }}>
                        <Ico size={18} style={{ color: active ? awardColor(form.color) : 'var(--kmt-muted)' }} />
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="kmt-label mb-2">{t('ma.color')}</div>
                <div className="flex flex-wrap gap-2" data-testid="award-color-picker">
                  {Object.keys(AWARD_COLORS).map((k) => (
                    <button key={k} onClick={() => setForm({ ...form, color: k })} data-testid={`award-color-${k}`}
                      className="w-8 h-8 rounded-full transition-transform hover:scale-110"
                      style={{ background: AWARD_COLORS[k], outline: form.color === k ? '2px solid var(--kmt-text)' : 'none', outlineOffset: 2 }} />
                  ))}
                </div>
              </div>
            </div>
            {/* Live preview */}
            <div className="flex flex-col">
              <div className="kmt-label mb-2">{t('ma.preview')}</div>
              <div className="flex-1 rounded-xl flex flex-col items-center justify-center p-8 text-center gap-3"
                style={{ background: 'var(--kmt-panel-2)', border: '1px solid var(--kmt-border)' }} data-testid="award-preview">
                <div className="flex items-center justify-center rounded-full" style={{ width: 86, height: 86, background: `${awardColor(form.color)}22` }}>
                  <PreviewIcon size={44} style={{ color: awardColor(form.color) }} />
                </div>
                <div className="font-display font-bold text-lg" style={{ color: awardColor(form.color) }}>{form.title || t('ma.titlePlaceholder')}</div>
                {form.description && <div className="text-sm" style={{ color: 'var(--kmt-muted)' }}>{form.description}</div>}
                {form.operator && <div className="kmt-label" style={{ fontSize: 10 }}>{t('ma.for')} {form.operator}</div>}
              </div>
              <button className="kmt-btn-gold flex items-center justify-center gap-2 mt-4" disabled={saving} onClick={give} data-testid="give-award-btn">
                {saving ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}{t('ma.give')}
              </button>
            </div>
          </div>
        </div>

        {/* Given awards */}
        <div className="kmt-panel p-6" data-testid="given-awards-panel">
          <div className="flex items-center gap-2 mb-4">
            <Award size={17} style={{ color: 'var(--kmt-gold)' }} />
            <div className="font-display font-semibold">{t('ma.givenTitle')} ({awards.length})</div>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-16" style={{ color: 'var(--kmt-muted)' }}><Loader2 className="animate-spin" size={26} /></div>
          ) : awards.length === 0 ? (
            <div className="text-center py-12" style={{ color: 'var(--kmt-muted)' }} data-testid="given-awards-empty">{t('ma.empty')}</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {awards.map((a) => {
                const Ico = awardIcon(a.icon);
                return (
                  <div key={a.id} className="flex items-start gap-3 rounded-lg px-4 py-3" data-testid={`given-award-${a.id}`}
                    style={{ background: 'var(--kmt-panel-2)', border: '1px solid var(--kmt-border)' }}>
                    <div className="flex items-center justify-center rounded-full shrink-0" style={{ width: 42, height: 42, background: `${awardColor(a.color)}22` }}>
                      <Ico size={21} style={{ color: awardColor(a.color) }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-medium truncate" style={{ color: awardColor(a.color) }}>{a.title}</div>
                      <div className="text-sm truncate">{a.operator}</div>
                      {a.description && <div className="text-xs mt-0.5" style={{ color: 'var(--kmt-muted)' }}>{a.description}</div>}
                    </div>
                    <button onClick={() => remove(a)} title={t('ma.removeTip')} data-testid={`delete-award-${a.id}`} style={{ color: 'var(--kmt-muted)' }} className="hover:text-[var(--kmt-red)] shrink-0"><Trash2 size={15} /></button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
